import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Megaphone, Copy, Link2, Link2Off, Download, MessageCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

export default function Campaigns({ portal }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [attendanceAt, setAttendanceAt] = useState("");
  const [capacity, setCapacity] = useState("");
  const [privateRoomPrice, setPrivateRoomPrice] = useState("");
  const [whatsappGroupLink, setWhatsappGroupLink] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/campaigns", { params: { portal } });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [portal]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const payload = { name: name.trim(), portal };
      if (portal === "commercial") {
        if (amount) payload.amount = Number(amount);
        if (attendanceAt) payload.attendance_at = new Date(attendanceAt).toISOString();
        if (capacity) payload.capacity = Number(capacity);
        if (privateRoomPrice) payload.private_room_price = Number(privateRoomPrice);
        if (whatsappGroupLink) payload.whatsapp_group_link = whatsappGroupLink.trim();
      }
      await api.post("/campaigns", payload);
      toast.success("تمت إضافة الحملة");
      setName(""); setAmount(""); setAttendanceAt(""); setCapacity(""); setPrivateRoomPrice(""); setWhatsappGroupLink("");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyLink = (token) => {
    // Use the /api/og/campaign/... share URL so WhatsApp shows association logo+name preview card
    const url = `${window.location.origin}/api/og/campaign/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("تم نسخ الرابط (مع معاينة الجمعية على واتساب)");
  };

  const toggleLink = async (c) => {
    try {
      await api.patch(`/campaigns/${c.id}`, { is_link_active: !c.is_link_active });
      toast.success(c.is_link_active ? "تم إيقاف الرابط" : "تم تفعيل الرابط");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadVcf = async (c) => {
    try {
      const r = await api.get(`/campaigns/${c.id}/vcard`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/vcard;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.name}-contacts.vcf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("تم تحميل جهات الاتصال (.vcf)");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const editWhatsAppLink = async (c) => {
    const val = window.prompt("أدخل/عدّل رابط مجموعة الواتساب لهذه الحملة:", c.whatsapp_group_link || "");
    if (val === null) return;
    try {
      await api.patch(`/campaigns/${c.id}`, { whatsapp_group_link: val.trim() });
      toast.success(val.trim() ? "تم حفظ الرابط" : "تم إزالة الرابط");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        title="إدارة الحملات"
        subtitle={`عدد الحملات: ${items.length} — الترقيم تلقائي مستقل لكل بوابة`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="campaign-add-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">
                <Plus className="h-4 w-4 ml-2" /> حملة جديدة
              </Button>
            </DialogTrigger>
            <DialogContent dir="rtl">
              <DialogHeader><DialogTitle className="text-right">إنشاء حملة جديدة</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cname">اسم الحملة</Label>
                  <Input id="cname" data-testid="campaign-name-input" value={name}
                    onChange={(e) => setName(e.target.value)} placeholder="مثال: عمرة رمضان 1447" required />
                </div>
                {portal === "commercial" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="camount">المبلغ المطلوب لكل مستفيد (ر.س)</Label>
                      <Input id="camount" type="number" step="0.01" data-testid="campaign-amount-input"
                        value={amount} onChange={(e) => setAmount(e.target.value)}
                        placeholder="مثال: 135" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cdate">تاريخ ووقت الحضور</Label>
                      <Input id="cdate" type="datetime-local" data-testid="campaign-attendance-input"
                        value={attendanceAt} onChange={(e) => setAttendanceAt(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ccap">العدد الأقصى للمقاعد (يشمل المرافقين)</Label>
                      <Input id="ccap" type="number" min="1" data-testid="campaign-capacity-input"
                        value={capacity} onChange={(e) => setCapacity(e.target.value)}
                        placeholder="اترك فارغاً بدون حد" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cprp">سعر الغرفة الخاصة (ر.س) — يُعرض فقط عند اختيار مرافق واحد</Label>
                      <Input id="cprp" type="number" step="0.01" min="0" data-testid="campaign-private-room-input"
                        value={privateRoomPrice} onChange={(e) => setPrivateRoomPrice(e.target.value)}
                        placeholder="مثال: 150 · اتركه فارغاً لتعطيل الخيار" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cwa">رابط مجموعة واتساب (اختياري)</Label>
                      <Input id="cwa" type="url" dir="ltr" data-testid="campaign-whatsapp-input"
                        value={whatsappGroupLink} onChange={(e) => setWhatsappGroupLink(e.target.value)}
                        placeholder="https://chat.whatsapp.com/..." />
                      <p className="text-xs text-slate-500">
                        سيُعرض للمستفيد ضمن تذكرته لينضم بنفسه بضغطة واحدة.
                      </p>
                    </div>
                  </>
                )}
                <DialogFooter>
                  <Button type="submit" data-testid="campaign-save-btn" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-800 text-white">
                    {busy ? "جارٍ الحفظ..." : "حفظ"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="border-emerald-900/10 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-16 text-center">
            <Megaphone className="h-10 w-10 text-emerald-900/30 mx-auto mb-3" />
            <p className="text-slate-500">لا توجد حملات بعد. ابدأ بإنشاء أول حملة.</p>
          </div>
        ) : (
          <table className="w-full text-right" data-testid="campaigns-table">
            <thead className="bg-emerald-950 text-white">
              <tr>
                <th className="p-4 font-heading font-bold">#</th>
                <th className="p-4 font-heading font-bold">اسم الحملة</th>
                <th className="p-4 font-heading font-bold">تاريخ الإنشاء</th>
                {portal === "commercial" && <th className="p-4 font-heading font-bold">المبلغ / الحضور</th>}
                {portal === "commercial" && <th className="p-4 font-heading font-bold">رابط التسجيل الذاتي</th>}
                <th className="p-4 font-heading font-bold w-24">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c, i) => (
                <tr key={c.id} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                  <td className="p-4 font-bold text-amber-700 tabular-nums">{c.number}</td>
                  <td className="p-4 text-emerald-950 font-medium">{c.name}</td>
                  <td className="p-4 text-slate-600 text-sm">{new Date(c.created_at).toLocaleDateString("ar-SA")}</td>
                  {portal === "commercial" && (
                    <td className="p-4 text-sm">
                      <div className="text-emerald-900 font-bold tabular-nums">{c.amount ? `${c.amount} ر.س` : "—"}</div>
                      {c.attendance_at && (
                        <div className="text-slate-500 text-xs mt-0.5">
                          {new Date(c.attendance_at).toLocaleString("ar-SA", { dateStyle: "short", timeStyle: "short" })}
                        </div>
                      )}
                      {c.capacity && (
                        <div className="text-amber-700 text-xs mt-1">السعة: {c.capacity} مقعد</div>
                      )}
                    </td>
                  )}
                  {portal === "commercial" && (
                    <td className="p-4">
                      {c.public_token ? (
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${c.is_link_active ? "bg-emerald-100 text-emerald-900" : "bg-slate-200 text-slate-600"}`}>
                            {c.is_link_active ? "مفعل" : "متوقف"}
                          </span>
                          <Button size="sm" variant="ghost" data-testid={`campaign-copy-link-${c.id}`}
                            onClick={() => copyLink(c.public_token)} className="text-emerald-800 hover:bg-emerald-50">
                            <Copy className="h-4 w-4 ml-1" /> نسخ الرابط
                          </Button>
                          <Button size="sm" variant="ghost" data-testid={`campaign-toggle-link-${c.id}`}
                            onClick={() => toggleLink(c)} className={c.is_link_active ? "text-amber-700 hover:bg-amber-50" : "text-emerald-700 hover:bg-emerald-50"}>
                            {c.is_link_active ? <Link2Off className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
                          </Button>
                        </div>
                      ) : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                  )}
                  <td className="p-4">
                    <div className="flex gap-1 items-center">
                      {portal === "commercial" && (
                        <>
                          <Button size="sm" variant="ghost" data-testid={`campaign-vcard-${c.id}`}
                            onClick={() => downloadVcf(c)} title="تحميل جهات الاتصال (vCard) للمستفيدين المؤكدين"
                            className="text-emerald-800 hover:bg-emerald-50">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" data-testid={`campaign-whatsapp-${c.id}`}
                            onClick={() => editWhatsAppLink(c)}
                            title={c.whatsapp_group_link ? "تعديل رابط مجموعة الواتساب" : "إضافة رابط مجموعة الواتساب"}
                            className={c.whatsapp_group_link ? "text-emerald-700 hover:bg-emerald-50" : "text-slate-500 hover:bg-slate-100"}>
                            <MessageCircle className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {user?.role === "admin" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" data-testid={`campaign-delete-${c.id}`}
                              className="text-red-600 hover:bg-red-50">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent dir="rtl">
                            <AlertDialogHeader>
                              <AlertDialogTitle>حذف الحملة؟</AlertDialogTitle>
                              <AlertDialogDescription>
                                لا يمكن التراجع. لن يتم الحذف إذا كانت مرتبطة بسجلات.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>إلغاء</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(c.id)} className="bg-red-600 hover:bg-red-700">
                                حذف
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
